import qs from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// COMPONENTS
import { ApiErrorBanner, DisplayDate, Loading } from 'src/components';
import { OGOnlyWrapper } from 'src/components/hibana';
import { Templates } from 'src/components/images';
import { PageLink } from 'src/components/links';
import { Box, Page, Panel, Stack, Tabs, Tag } from 'src/components/matchbox';
import { Heading, PageDescription } from 'src/components/text';
import AlertCollection from './components/AlertCollection';
import ConfiguredAlerts from './components/ConfiguredAlerts/ConfiguredAlerts';
import RecentIncidents from './components/RecentIncidents/RecentIncidents';

// HELPERS
import { isAccountUiOptionSet } from 'src/helpers/conditions/account';

// ACTIONS
import { listAlerts, listIncidents } from 'src/actions/alerts';

// SELECTORES
import {
  selectAlertsList,
  selectIncidentsList,
  selectRecentlyTriggeredAlerts
} from 'src/selectors/alerts';

// CONSTANTS
import { METRICS } from './constants/formConstants';

// STYLES
import styles from './ListPage.module.scss';

// TODO-TS: this is literally an TS Enum
const ListPageTabsEnum = {
  0: 'recent',
  recent: 0,
  1: 'configured',
  configured: 1
};

export default function ListPage() {
  const { search } = useLocation();
  const params = qs.parse(search);
  const { push } = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const error = useSelector((state) => state.alerts.listError);
  const loading = useSelector((state) => state.alerts.listPending);
  const recentlyTriggeredAlerts = useSelector(selectRecentlyTriggeredAlerts);
  const alerts = useSelector(selectAlertsList);
  const incidents = useSelector(selectIncidentsList);
  const [tab, setTab] = useState(ListPageTabsEnum[params.tab] || ListPageTabsEnum.recent);

  const isAlertsV2 = isAccountUiOptionSet('new_alerts_enabled')(state);

  const handleTabChange = useCallback(
    (tabIndex) => {
      const tab = ListPageTabsEnum[tabIndex];
      push(`/alerts?tab=${tab}`);
      setTab(tabIndex);
    },
    [push]
  );

  useEffect(() => {
    dispatch(listAlerts(isAlertsV2));
    if (isAlertsV2) dispatch(listIncidents(isAlertsV2));
  }, [dispatch, isAlertsV2]);

  if (loading) return <Loading />;

  if (error) {
    return (
      <Page title="Alerts">
        <ApiErrorBanner
          message="Sorry, we seem to have had some trouble loading your alerts."
          errorDetails={error.message}
          reload={() => {
            dispatch(listAlerts(isAlertsV2));
            if (isAlertsV2) dispatch(listIncidents(isAlertsV2));
          }}
        />
      </Page>
    );
  }

  const v1Page = (
    <Page
      title="Alerts"
      primaryAction={{ content: 'Create an Alert', to: '/alerts/create', as: PageLink }}
      empty={{
        show: !error && alerts.length === 0,
        image: Templates,
        title: 'Create an Alert',
        content: <p>Manage notifications that alert you of performance problems.</p>
      }}
    >
      <PageDescription>
        Use alerts to be notified when important changes occur in your Health Score, bounce rates,
        and email usage.
      </PageDescription>

      <Box mb="500">
        {recentlyTriggeredAlerts.length > 0 ? (
          <div data-id="recent-incidents">
            <Stack space="400">
              <Heading as="h2" looksLike="h3">
                Recent Incidents
              </Heading>

              <div className={styles.RecentIncidents}>
                {recentlyTriggeredAlerts.map((alert) => {
                  return (
                    <Panel.LEGACY accent key={alert.id}>
                      <Panel.LEGACY.Section>
                        <OGOnlyWrapper as="div" className={styles.PanelStack}>
                          <Stack>
                            {/* Extra <div> here prevents flex parent from stretching tag to full width */}
                            <div>
                              <Tag>{METRICS[alert.metric]}</Tag>
                            </div>

                            <PageLink
                              className={styles.AlertName}
                              to={`/alerts/details/${alert.id}`}
                              data-id="link-alert-name"
                            >
                              {alert.name}
                            </PageLink>
                          </Stack>
                        </OGOnlyWrapper>
                      </Panel.LEGACY.Section>

                      <Panel.LEGACY.Section className={styles.Footer}>
                        <Box color="gray.700" fontSize="200">
                          <DisplayDate
                            timestamp={alert.last_triggered_timestamp}
                            formattedDate={alert.last_triggered_formatted}
                          />
                        </Box>
                      </Panel.LEGACY.Section>
                    </Panel.LEGACY>
                  );
                })}
              </div>
            </Stack>
          </div>
        ) : null}
      </Box>

      <AlertCollection alerts={alerts} />
    </Page>
  );

  const v2Page = (
    <Page
      title="Alerts"
      primaryAction={{ content: 'Create an Alert', to: '/alerts/create', as: PageLink }}
      empty={{
        show: !error && alerts.length === 0,
        image: Templates,
        title: 'Create an Alert',
        content: <p>Manage notifications that alert you of performance problems.</p>
      }}
    >
      <Stack>
        <Tabs
          selected={tab}
          onSelect={handleTabChange}
          tabs={[{ content: 'Recent Incidents' }, { content: 'Configured Alerts' }]}
        />
        {tab === ListPageTabsEnum.recent && <RecentIncidents incidents={incidents} />}
        {tab === ListPageTabsEnum.configured && <ConfiguredAlerts alerts={alerts} />}
      </Stack>
    </Page>
  );

  return isAlertsV2 ? v2Page : v1Page;
}
